import {
  Domain,
  DomainFilterStatus,
  DomainsQueryParams,
  Folder,
  FoldersQueryParams,
  GlobalFoldersQueryParams,
} from '@/types/domain.types';
import { create } from 'zustand';

// Persistance du périmètre « Mes dossiers » / « Tous les dossiers » (préférence
// utilisateur, indépendante de l'organisation courante).
const FOLDERS_SCOPE_STORAGE_KEY = 'openpaye_folders_scope';

function loadPersistedFoldersScope(): 'all' | 'mine' {
  try {
    const value = localStorage.getItem(FOLDERS_SCOPE_STORAGE_KEY);
    return value === 'mine' ? 'mine' : 'all';
  } catch {
    return 'all';
  }
}

function persistFoldersScope(scope: 'all' | 'mine'): void {
  try {
    localStorage.setItem(FOLDERS_SCOPE_STORAGE_KEY, scope);
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

interface DomainsState {
  // Domains list
  domains: Domain[];
  isLoadingDomains: boolean;

  // Domain detail
  selectedDomain: Domain | null;
  isLoadingDomainDetail: boolean;

  // Folders for selected domain
  folders: Folder[];
  isLoadingFolders: boolean;
  foldersCurrentPage: number;
  foldersTotalPages: number;
  foldersTotalItems: number;
  foldersHasNext: boolean;
  foldersHasPrev: boolean;
  foldersSearchQuery: string;
  foldersStatusFilter: 'all' | 'active' | 'inactive' | 'archived';
  foldersResponsableFilter: string; // new: free-text filter for responsable
  foldersSortBy: 'name' | 'createdAt' | 'updatedAt' | 'totalDocuments';
  foldersSortOrder: 'ASC' | 'DESC';
  foldersLimit: number;

  // Global folders (cross-domain)
  globalFolders: Folder[];
  isLoadingGlobalFolders: boolean;
  globalFoldersCurrentPage: number;
  globalFoldersTotalPages: number;
  globalFoldersTotalItems: number;
  globalFoldersHasNext: boolean;
  globalFoldersHasPrev: boolean;
  globalFoldersSearchQuery: string;
  globalFoldersStatusFilter: 'all' | 'active' | 'inactive' | 'archived';
  globalFoldersSyncStatusFilter: 'all' | 'synced' | 'not_synced';
  globalFoldersDomainNameFilter: string;
  globalFoldersResponsableFilter: string; // new: free-text filter for responsable
  // Périmètre : 'mine' = uniquement mes dossiers, 'all' = tous (défaut). Persisté.
  globalFoldersScope: 'all' | 'mine';
  globalFoldersSortBy: 'name' | 'createdAt' | 'updatedAt' | 'totalDocuments';
  globalFoldersSortOrder: 'ASC' | 'DESC';
  globalFoldersLimit: number;

  // Folder selection for bulk operations
  selectedFolders: Set<string>;

  // Pagination
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasNext: boolean;
  hasPrev: boolean;

  // Filters and search
  searchQuery: string;
  statusFilter: DomainFilterStatus;
  sortBy: 'name' | 'createdAt' | 'lastSyncAt' | 'foldersCount';
  sortOrder: 'ASC' | 'DESC';

  // Sync operations
  isSyncing: boolean;
  syncingDomainId: string | null;

  // General
  error: string | null;
}

interface DomainsActions {
  // Domains list
  setDomains: (domains: Domain[]) => void;
  addDomain: (domain: Domain) => void;
  updateDomain: (uuid: string, updates: Partial<Domain>) => void;
  removeDomain: (uuid: string) => void;
  setLoadingDomains: (loading: boolean) => void;

  // Domain detail
  setSelectedDomain: (domain: Domain | null) => void;
  setLoadingDomainDetail: (loading: boolean) => void;

  // Folders management
  setFolders: (folders: Folder[]) => void;
  addFolder: (folder: Folder) => void;
  updateFolder: (uuid: string, updates: Partial<Folder>) => void;
  removeFolder: (uuid: string) => void;
  setLoadingFolders: (loading: boolean) => void;
  setFoldersPagination: (pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasNext: boolean;
    hasPrev: boolean;
  }) => void;
  setFoldersCurrentPage: (page: number) => void;
  setFoldersSearchQuery: (query: string) => void;
  setFoldersStatusFilter: (status: 'all' | 'active' | 'inactive' | 'archived') => void;
  setFoldersResponsableFilter: (responsable: string) => void;
  setFoldersSortBy: (
    sortBy: 'name' | 'createdAt' | 'updatedAt' | 'totalDocuments'
  ) => void;
  setFoldersSortOrder: (order: 'ASC' | 'DESC') => void;
  setFoldersLimit: (limit: number) => void;
  getFoldersQueryParams: () => FoldersQueryParams;
  clearFolders: () => void;

  // Pagination
  setPagination: (pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasNext: boolean;
    hasPrev: boolean;
  }) => void;
  setCurrentPage: (page: number) => void;

  // Filters and search
  setSearchQuery: (query: string) => void;
  setStatusFilter: (status: DomainFilterStatus) => void;
  setSortBy: (
    sortBy: 'name' | 'createdAt' | 'lastSyncAt' | 'foldersCount'
  ) => void;
  setSortOrder: (order: 'ASC' | 'DESC') => void;
  getQueryParams: () => DomainsQueryParams;

  // Sync operations
  setSyncing: (syncing: boolean) => void;
  setSyncingDomainId: (domainId: string | null) => void;

  // General
  setError: (error: string | null) => void;
  clearError: () => void;
  reset: () => void;

  // Global folders management
  setGlobalFolders: (folders: Folder[]) => void;
  setLoadingGlobalFolders: (loading: boolean) => void;
  setGlobalFoldersPagination: (pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasNext: boolean;
    hasPrev: boolean;
  }) => void;
  setGlobalFoldersCurrentPage: (page: number) => void;
  setGlobalFoldersSearchQuery: (query: string) => void;
  setGlobalFoldersStatusFilter: (status: 'all' | 'active' | 'inactive' | 'archived') => void;
  setGlobalFoldersSyncStatusFilter: (
    status: 'all' | 'synced' | 'not_synced'
  ) => void;
  setGlobalFoldersDomainNameFilter: (domainName: string) => void;
  setGlobalFoldersResponsableFilter: (responsable: string) => void;
  setGlobalFoldersScope: (scope: 'all' | 'mine') => void;
  setGlobalFoldersSortBy: (
    sortBy: 'name' | 'createdAt' | 'updatedAt' | 'totalDocuments'
  ) => void;
  setGlobalFoldersSortOrder: (order: 'ASC' | 'DESC') => void;
  getGlobalFoldersQueryParams: () => GlobalFoldersQueryParams;
  clearGlobalFolders: () => void;
  setGlobalFoldersLimit: (limit: number) => void;

  // Folder selection for bulk operations
  toggleFolderSelection: (folderUuid: string) => void;
  selectAllFolders: (folderUuids: string[]) => void;
  clearFolderSelection: () => void;
  isSelected: (folderUuid: string) => boolean;
}

const initialState: DomainsState = {
  domains: [],
  isLoadingDomains: false,
  selectedDomain: null,
  isLoadingDomainDetail: false,
  folders: [],
  isLoadingFolders: false,
  foldersCurrentPage: 1,
  foldersTotalPages: 1,
  foldersTotalItems: 0,
  foldersHasNext: false,
  foldersHasPrev: false,
  foldersSearchQuery: '',
  foldersStatusFilter: 'all',
  foldersResponsableFilter: '',
  foldersSortBy: 'name',
  foldersSortOrder: 'ASC',
  foldersLimit: 20,
  globalFolders: [],
  isLoadingGlobalFolders: false,
  globalFoldersCurrentPage: 1,
  globalFoldersTotalPages: 1,
  globalFoldersTotalItems: 0,
  globalFoldersHasNext: false,
  globalFoldersHasPrev: false,
  globalFoldersSearchQuery: '',
  globalFoldersStatusFilter: 'all',
  globalFoldersSyncStatusFilter: 'all',
  globalFoldersDomainNameFilter: '',
  globalFoldersResponsableFilter: '',
  globalFoldersScope: loadPersistedFoldersScope(),
  globalFoldersSortBy: 'name',
  globalFoldersSortOrder: 'ASC',
  globalFoldersLimit: 20,
  selectedFolders: new Set(),
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  hasNext: false,
  hasPrev: false,
  searchQuery: '',
  statusFilter: 'all',
  sortBy: 'name',
  sortOrder: 'ASC',
  isSyncing: false,
  syncingDomainId: null,
  error: null,
};

export const useDomainsStore = create<DomainsState & DomainsActions>()(
  (set, get) => ({
    ...initialState,

    // Domains list
    setDomains: domains => set({ domains }),
    addDomain: domain => {
      const currentDomains = get().domains;
      set({ domains: [...currentDomains, domain] });
    },
    updateDomain: (uuid, updates) => {
      const currentDomains = get().domains;
      const updatedDomains = currentDomains.map(domain =>
        domain.uuid === uuid ? { ...domain, ...updates } : domain
      );
      set({ domains: updatedDomains });

      // Update selected domain if it's the one being updated
      const selectedDomain = get().selectedDomain;
      if (selectedDomain?.uuid === uuid) {
        set({ selectedDomain: { ...selectedDomain, ...updates } });
      }
    },
    removeDomain: uuid => {
      const currentDomains = get().domains;
      const updatedDomains = currentDomains.filter(
        domain => domain.uuid !== uuid
      );
      set({ domains: updatedDomains });

      // Clear selected domain if it's the one being removed
      const selectedDomain = get().selectedDomain;
      if (selectedDomain?.uuid === uuid) {
        set({ selectedDomain: null });
      }
    },
    setLoadingDomains: isLoadingDomains => set({ isLoadingDomains }),

    // Domain detail
    setSelectedDomain: selectedDomain => set({ selectedDomain }),
    setLoadingDomainDetail: isLoadingDomainDetail =>
      set({ isLoadingDomainDetail }),

    // Folders management
    setFolders: folders => set({ folders }),
    addFolder: folder => {
      const currentFolders = get().folders;
      set({ folders: [...currentFolders, folder] });
    },
    updateFolder: (uuid, updates) => {
      const currentFolders = get().folders;
      const updatedFolders = currentFolders.map(folder =>
        folder.uuid === uuid ? { ...folder, ...updates } : folder
      );
      set({ folders: updatedFolders });
    },
    removeFolder: uuid => {
      const currentFolders = get().folders;
      const updatedFolders = currentFolders.filter(
        folder => folder.uuid !== uuid
      );
      set({ folders: updatedFolders });
    },
    setLoadingFolders: isLoadingFolders => set({ isLoadingFolders }),
    setFoldersPagination: pagination =>
      set({
        foldersCurrentPage: pagination.currentPage,
        foldersTotalPages: pagination.totalPages,
        foldersTotalItems: pagination.totalItems,
        foldersHasNext: pagination.hasNext,
        foldersHasPrev: pagination.hasPrev,
      }),
    setFoldersCurrentPage: foldersCurrentPage => set({ foldersCurrentPage }),
    setFoldersSearchQuery: foldersSearchQuery => set({ foldersSearchQuery }),
    setFoldersStatusFilter: foldersStatusFilter => set({ foldersStatusFilter }),
    setFoldersResponsableFilter: foldersResponsableFilter =>
      set({ foldersResponsableFilter }),
    setFoldersSortBy: foldersSortBy => set({ foldersSortBy }),
    setFoldersSortOrder: foldersSortOrder => set({ foldersSortOrder }),
    setFoldersLimit: foldersLimit => set({ foldersLimit, foldersCurrentPage: 1 }),
    getFoldersQueryParams: () => {
      const state = get();
      return {
        page: state.foldersCurrentPage,
        limit: state.foldersLimit,
        search: state.foldersSearchQuery || undefined,
        status:
          state.foldersStatusFilter !== 'all'
            ? state.foldersStatusFilter
            : undefined,
        responsable: state.foldersResponsableFilter || undefined,
        sortBy: state.foldersSortBy,
        sortOrder: state.foldersSortOrder,
      };
    },
    clearFolders: () =>
      set({
        folders: [],
        foldersCurrentPage: 1,
        foldersTotalPages: 1,
        foldersTotalItems: 0,
        foldersHasNext: false,
        foldersHasPrev: false,
      }),

    // Pagination
    setPagination: pagination => set(pagination),
    setCurrentPage: currentPage => set({ currentPage }),

    // Filters and search
    setSearchQuery: searchQuery => set({ searchQuery }),
    setStatusFilter: statusFilter => set({ statusFilter }),
    setSortBy: sortBy => set({ sortBy }),
    setSortOrder: sortOrder => set({ sortOrder }),
    getQueryParams: () => {
      const state = get();
      return {
        page: state.currentPage,
        limit: 10, // Default limit
        search: state.searchQuery || undefined,
        status: state.statusFilter !== 'all' ? state.statusFilter : undefined,
        sortBy: state.sortBy,
        sortOrder: state.sortOrder,
      };
    },

    // Sync operations
    setSyncing: isSyncing => set({ isSyncing }),
    setSyncingDomainId: syncingDomainId => set({ syncingDomainId }),

    // General
    setError: error => set({ error }),
    clearError: () => set({ error: null }),
    reset: () => set(initialState),

    // Global folders management
    setGlobalFolders: folders => set({ globalFolders: folders }),
    setLoadingGlobalFolders: isLoadingGlobalFolders =>
      set({ isLoadingGlobalFolders }),
    setGlobalFoldersPagination: pagination =>
      set({
        globalFoldersCurrentPage: pagination.currentPage,
        globalFoldersTotalPages: pagination.totalPages,
        globalFoldersTotalItems: pagination.totalItems,
        globalFoldersHasNext: pagination.hasNext,
        globalFoldersHasPrev: pagination.hasPrev,
      }),
    setGlobalFoldersCurrentPage: globalFoldersCurrentPage =>
      set({ globalFoldersCurrentPage }),
    setGlobalFoldersSearchQuery: globalFoldersSearchQuery =>
      set({ globalFoldersSearchQuery }),
    setGlobalFoldersStatusFilter: globalFoldersStatusFilter =>
      set({ globalFoldersStatusFilter }),
    setGlobalFoldersSyncStatusFilter: globalFoldersSyncStatusFilter =>
      set({ globalFoldersSyncStatusFilter }),
    setGlobalFoldersDomainNameFilter: globalFoldersDomainNameFilter =>
      set({ globalFoldersDomainNameFilter }),
    setGlobalFoldersResponsableFilter: globalFoldersResponsableFilter =>
      set({ globalFoldersResponsableFilter }),
    setGlobalFoldersScope: globalFoldersScope => {
      persistFoldersScope(globalFoldersScope);
      set({ globalFoldersScope, globalFoldersCurrentPage: 1 });
    },
    setGlobalFoldersSortBy: globalFoldersSortBy => set({ globalFoldersSortBy }),
    setGlobalFoldersSortOrder: globalFoldersSortOrder =>
      set({ globalFoldersSortOrder }),
    getGlobalFoldersQueryParams: () => {
      const state = get();
      return {
        page: state.globalFoldersCurrentPage,
        limit: state.globalFoldersLimit,
        search: state.globalFoldersSearchQuery || undefined,
        status:
          state.globalFoldersStatusFilter !== 'all'
            ? state.globalFoldersStatusFilter
            : undefined,
        syncStatus:
          state.globalFoldersSyncStatusFilter !== 'all'
            ? state.globalFoldersSyncStatusFilter
            : undefined,
        domainName: state.globalFoldersDomainNameFilter || undefined,
        responsable: state.globalFoldersResponsableFilter || undefined,
        // N'envoyer scope que pour 'mine' — absent = comportement par défaut (tous).
        scope: state.globalFoldersScope === 'mine' ? 'mine' : undefined,
        sortBy: state.globalFoldersSortBy,
        sortOrder: state.globalFoldersSortOrder,
      };
    },
    clearGlobalFolders: () =>
      set({
        globalFolders: [],
        globalFoldersCurrentPage: 1,
        globalFoldersTotalPages: 1,
        globalFoldersTotalItems: 0,
        globalFoldersHasNext: false,
        globalFoldersHasPrev: false,
      }),
    setGlobalFoldersLimit: globalFoldersLimit => set({ globalFoldersLimit, globalFoldersCurrentPage: 1 }),

    // Folder selection for bulk operations
    toggleFolderSelection: folderUuid => {
      const currentSelection = get().selectedFolders;
      const newSelection = new Set(currentSelection);

      if (newSelection.has(folderUuid)) {
        newSelection.delete(folderUuid);
      } else {
        newSelection.add(folderUuid);
      }

      set({ selectedFolders: newSelection });
    },
    selectAllFolders: folderUuids => {
      set({ selectedFolders: new Set(folderUuids) });
    },
    clearFolderSelection: () => {
      set({ selectedFolders: new Set() });
    },
    isSelected: folderUuid => {
      return get().selectedFolders.has(folderUuid);
    },
  })
);
